import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Share } from '@capacitor/share';
import { isSafeHttpUrl } from '@/lib/url';

/**
 * Opens an external link in an in-app browser (Custom Tab / SFSafariViewController)
 * on native, keeping the user inside the app context instead of being thrown out
 * to Chrome without warning. Falls back to a new tab on the web.
 *
 * Only http(s) URLs are opened — event links come from scraped third-party sources,
 * so `javascript:`/`data:` schemes are rejected to avoid code execution.
 */
export async function openExternal(url: string) {
  if (!isSafeHttpUrl(url)) return;
  if (Capacitor.isNativePlatform()) {
    try {
      await Browser.open({ url });
      return;
    } catch {
      /* fallthrough to web behaviour */
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export type ShareOutcome = 'shared' | 'copied' | 'failed';

/**
 * Triggers the native share sheet (Android ACTION_SEND / iOS share sheet) via the
 * Capacitor Share plugin on device. Falls back to the Web Share API, then to a
 * clipboard copy when nothing else is available. Returns how the link left the
 * app : 'copied' n'a aucune UI système, l'appelant doit afficher un feedback.
 */
export async function shareLink({ title, url, text }: { title?: string; url: string; text?: string }): Promise<ShareOutcome> {
  if (Capacitor.isNativePlatform()) {
    try {
      await Share.share({ title, text, url, dialogTitle: title });
    } catch {
      /* annulé ou indisponible */
    }
    return 'shared';
  }

  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title, text, url });
    } catch {
      /* annulé */
    }
    return 'shared';
  }

  try {
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch {
    return 'failed';
  }
}
